import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, Loader2, Link2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  useMicrosoftOAuthStatus,
  useInitiateMicrosoftOAuth,
  useCompleteMicrosoftOAuth,
  useDisconnectMicrosoftOAuth,
} from "@/hooks/use-microsoft-oauth";
import { useIntegrations, useTestIntegration } from "@/hooks/use-api";
import { ApiError } from "@/lib/api";
import { describeTestError } from "@/lib/integration-grid";
import { sourceTypeLabel } from "@/lib/integration-meta";
import { EntraLicensePanel } from "@/components/integrations/entra-license-panel";

interface ConnectMicrosoftProps {
  onConnected?: () => void;
  showAdvancedLink?: boolean;
  onAdvancedClick?: () => void;
}

export function ConnectMicrosoft({ onConnected, showAdvancedLink, onAdvancedClick }: ConnectMicrosoftProps) {
  const { data: status, isLoading: statusLoading } = useMicrosoftOAuthStatus();
  const initiate = useInitiateMicrosoftOAuth();
  const complete = useCompleteMicrosoftOAuth();
  const disconnect = useDisconnectMicrosoftOAuth();
  const { data: integrationsData } = useIntegrations();
  const testIntegration = useTestIntegration();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [capError, setCapError] = useState<string | null>(null);
  const [recheckError, setRecheckError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const popupRef = useRef<Window | null>(null);

  // The status endpoint reports the entra licence state but not the
  // integration id; resolve it from the integrations list so the
  // "re-check licence" action can POST /integrations/<id>/test.
  const entraId = integrationsData?.integrations?.find(
    (i) => i.source_type === "entra_id",
  )?.id;

  const handleRecheck = () => {
    if (!entraId) return;
    setRecheckError(null);
    testIntegration.mutate(entraId, {
      onSuccess: () => {
        // /test persists the refreshed licence tier + sign-in flag on the
        // integration; refresh status (drives the panel) and the grid.
        qc.invalidateQueries({ queryKey: ["microsoft-oauth-status"] });
        qc.invalidateQueries({ queryKey: ["integrations"] });
      },
      onError: (err) => setRecheckError(describeTestError(err).primary),
    });
  };

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "microsoft-oauth-callback") return;

      setConnecting(true);
      window.removeEventListener("message", handleMessage);

      if (event.data.error) {
        setError(event.data.error_description || "Consent was denied");
        setConnecting(false);
        return;
      }

      complete.mutate(
        {
          admin_consent: event.data.admin_consent,
          tenant: event.data.tenant,
          state: event.data.state,
        },
        {
          onSuccess: () => {
            setConnecting(false);
            setError(null);
            onConnected?.();
          },
          onError: (err) => {
            setConnecting(false);
            // Trial-cap (#251): the shared callback reserves an endpoint cap
            // unit and can refuse with a 409 — render it as a clean upgrade
            // path rather than a raw error string.
            if (
              err instanceof ApiError &&
              err.status === 409 &&
              err.code === "trial_endpoint_cap_reached"
            ) {
              setCapError(err.message);
            } else {
              setError(err.message || "Failed to complete connection");
            }
          },
        }
      );
    },
    [complete, onConnected]
  );

  useEffect(() => {
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  const handleConnect = async () => {
    setError(null);
    setCapError(null);

    // Open popup immediately to avoid blocker
    const popup = window.open("about:blank", "microsoft-consent", "width=600,height=700,scrollbars=yes");
    popupRef.current = popup;

    try {
      const result = await initiate.mutateAsync();
      if (popup && !popup.closed) {
        popup.location.href = result.consent_url;
      }
      window.addEventListener("message", handleMessage);
    } catch (err) {
      popup?.close();
      setError(err instanceof Error ? err.message : "Failed to start consent flow");
    }
  };

  const handleDisconnect = () => {
    disconnect.mutate(undefined, {
      onError: (err) => setError(err.message),
    });
  };

  if (statusLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Connected state
  if (status?.connected) {
    return (
      <Card className="border-green-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <CardTitle className="text-lg">Microsoft Connected</CardTitle>
            </div>
            <Badge variant="outline" className="border-green-700 text-green-400">Active</Badge>
          </div>
          <CardDescription>
            Azure tenant {status.azure_tenant_id?.slice(0, 8)}... connected by {status.connected_by}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {status.integrations?.map((type) => (
              <Badge key={type} variant="secondary">{sourceTypeLabel(type)}</Badge>
            ))}
          </div>
          {status.entra && (
            <div className="mb-4">
              <EntraLicensePanel
                entra={status.entra}
                onRecheck={handleRecheck}
                recheckPending={testIntegration.isPending}
                recheckError={recheckError}
                recheckDisabled={!entraId}
              />
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={handleDisconnect} disabled={disconnect.isPending}>
            <Unlink className="h-4 w-4 mr-1" />
            {disconnect.isPending ? "Disconnecting..." : "Disconnect"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Not connected / error state
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-blue-500" />
          <CardTitle className="text-lg">Connect Microsoft</CardTitle>
        </div>
        <CardDescription>
          Connect your Microsoft 365 environment to enable security monitoring for:
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1 mb-4 text-sm text-muted-foreground">
          <li>Entra ID — sign-in and audit logs</li>
          <li>Microsoft 365 — email security</li>
          <li>Microsoft Defender — threat detection</li>
        </ul>
        <p className="text-xs text-muted-foreground mb-2">
          Your Global Admin will need to approve permissions in a Microsoft popup.
        </p>
        <p className="text-xs text-muted-foreground mb-4">
          Monitoring activates at go-live — connecting now just configures the integration.
        </p>

        {capError && (
          <div
            data-testid="entra-cap-notice"
            role="alert"
            className="rounded-md bg-yellow-500/10 p-3 mb-4 text-sm"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-yellow-500" />
              <span>{capError}</span>
            </div>
            <p className="mt-1 pl-6 text-muted-foreground">
              Upgrade your plan to connect additional Microsoft services.
            </p>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 mb-4 text-sm text-destructive"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={handleConnect} disabled={connecting || initiate.isPending}>
            {connecting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              "Connect Microsoft"
            )}
          </Button>
          {showAdvancedLink && (
            <Button variant="link" size="sm" onClick={onAdvancedClick}>
              Advanced Setup
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
